import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ProtectedRegion} from 'molbio/codon-optimization/reduceCodonOptimizationTab';
import * as sequtil from 'molbio/SeqUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {ellipsize} from 'util/NameUtil';

interface CodonOptimizationTabProtectRegionsPickerProps {
  protectedRegions: Array<ProtectedRegion>;
  onDelete: (region: ProtectedRegion) => void;
  onDeleteAll: () => void;
  onAdd: () => void;
  seqLength: number;
  startIndex: number;
  invalidProtectedRegionMessage: string | null;
  onSetSelection: (start: number, end: number) => void;
}

const CodonOptimizationTabProtectRegionsPicker = (
  props: CodonOptimizationTabProtectRegionsPickerProps
): JSX.Element => {
  const rows = props.protectedRegions.map((region: ProtectedRegion) => {
    const [start, end] = sequtil.modelRangeToDisplay(
      region.start,
      region.end,
      props.seqLength,
      props.startIndex
    );
    const name = region.name;
    return {
      name,
      region,
      range: `${start}-${end}`,
      length: sequtil.length(region.start, region.end, props.seqLength),
      delete: (
        <Cell className={css(styles.deleteCell)}>
          <BenchlingIcon
            onClick={(e) => {
              e.stopPropagation();
              props.onDelete(region);
            }}
            icon="delete"
            className={css(styles.deleteButton)}
          />
        </Cell>
      ),
    };
  });
  return (
    <div>
      <div>
        <Table
          numRows={rows.length}
          emptyTableText="Select a region to protect."
          onRowClick={(rowIndex: number) => {
            const {region} = rows[rowIndex];
            props.onSetSelection(region.start, region.end);
          }}
          getRowClassName={(rowIndex: number) => css(styles.tableRow)}
        >
          <Column header="Name" renderCell={(rowIndex: number) => ellipsize(rows[rowIndex].name, 50)} />
          <Column header="Region" renderCell={(rowIndex: number) => rows[rowIndex].range} />
          <Column header="Length" renderCell={(rowIndex: number) => rows[rowIndex].length} />
          <Column header="Remove" renderCell={(rowIndex: number) => rows[rowIndex].delete} />
        </Table>
      </div>
      <Tooltip
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        title={props.invalidProtectedRegionMessage !== null ? props.invalidProtectedRegionMessage : ''}
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        disabled={props.invalidProtectedRegionMessage === null}
      >
        <Button
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyle.TERTIARY_BLUE_POWDER}
          text={
            <div
              {...getTestProps({
                component: 'CodonOptimizationTabProtectRegionsPicker',
                element: 'addSelectedRegionButton',
              })}
            >
              <BenchlingIcon icon="note-text" />
              <span className={css(styles.buttonText)}>Add selected region</span>
            </div>
          }
          onClick={props.onAdd}
          // eslint-disable-next-line benchlint/no-eqeqeq-null
          disabled={props.invalidProtectedRegionMessage !== null}
        />
      </Tooltip>
      <Button
        buttonStyle={ButtonStyle.NEGATIVE}
        className={css(styles.deleteAll)}
        disabled={rows.length === 0}
        size={ButtonSize.SMALL}
        text={
          <div>
            <BenchlingIcon icon="delete" />
            <span className={css(styles.buttonText)}>Remove all protected regions</span>
          </div>
        }
        onClick={props.onDeleteAll}
      />
    </div>
  );
};

const styles = StyleSheet.create({
  protectRegions: {
    height: '100%',
    width: '100%',
  },
  deleteAll: {
    float: 'right',
  },
  deleteButton: {
    color: GRAY,
    ':hover': {
      color: RED,
    },
    cursor: 'pointer',
  },
  buttonText: {
    marginLeft: 5,
  },
  tableRow: {
    ':hover': {
      cursor: 'pointer',
    },
  },
  deleteCell: {
    padding: '15px 13px',
  },
});

export default CodonOptimizationTabProtectRegionsPicker;
